import { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { FieldErrors, UseFormReset, UseFormSetError } from 'react-hook-form';
import { useAppSelector } from '@store';
import {
  BaseellaStatus,
  BaseTransaction,
  DocumentStatus,
  formatBalance,
  formatDate,
  ResponseError,
  responseErrorsResolver,
  SignatureType,
  DocumentType,
  Document,
  GeneralLedgerTransaction,
  AccountType,
} from '@ui/src';
import {
  useCreateTransactionSignatureMutation,
  useLazyGetTransactionSignatureQuery,
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../../store/signature/service';
import { useLazyGetTransactionsAccountBalanceByIdQuery } from '../../general-ledger/store/services';
import {
  useAddCommentMutation,
  useAddDocumentToTransactionMutation,
  useChangeDocumentCategoryMutation,
  useChangeDocumentStatusMutation,
  useCreateTransactionChatMutation,
  useDeleteCommentMutation,
  useDeleteDocumentsMutation,
  useDeleteTransactionMutation,
  useEditCommentMutation,
  useLazyGetTransactionsCASPQuery,
  useLazyGetClientsOfficiasQuery,
  useLazyGetGeneralLedgerExpandedTransactionQuery,
  useLazyGetGeneralLedgerTransactionValuesQuery,
  useLazyGetTransactionChatsQuery,
  useLazyGetTransactionDocumentByIdQuery,
  useLazyGetTransactionDocumentFileQuery,
  useLazyGetTransactionsDocumentsQuery,
  useTransactionsUploadMutation,
  useLazyGetTransactionChangeLogsQuery,
  useLazyGetTransactionCommentsQuery,
  useAddCommentToTransactionMutation,
  useEditCommentInTransactionMutation,
  useDeleteCommentInTransactionMutation,
} from '../store/services';
import { ChatBody, Employee, UploadResponse } from '../type';
import { MfaToken, MfaType } from '../../../store/types';
import {
  useChangeTransactionStatusMutation,
  useLazyGetTransactionLogsQuery,
} from '../../../store/statuses/services';
import { useCheckDocumentStringNameMutation } from '../../../../../../store/service';

let timer: NodeJS.Timeout;
let timeout: NodeJS.Timeout;

const useBaseRequests = ({
  setError,
  transactionId,
  transaction,
  currentEmployee,
  sendMessage,
  setErrorMessage,
  createdTransactionId,
  statusUrl,
  setStatusChangeError,
  onSubmit,
  getTransactionsList,
  getStatusesTransactionById,
  onClearTimer,
  getTransaction,
  reset,
  handleClose,
  onClear,
  chatTitle,
  takeCount,
  search,
  transactionListUrl,
  errors,
  isDirty,
  documents,
  documentItem,
  setCollapseAllRows,
  updateIsError,
  paymentType,
  expandedGLTransaction,
  setUploadedFilesError,
  exchangeChatTitle,
  setRefundPopupVisible,
  transactionUrl,
}: {
  setError: UseFormSetError<any>;
  transactionId?: string;
  transaction?: BaseTransaction;
  currentEmployee?: Employee;
  sendMessage?: SendMessage;
  setErrorMessage: (data: string) => void;
  createdTransactionId?: string;
  statusUrl: string;
  setStatusChangeError: (data: string) => void;
  onSubmit: any;
  getTransactionsList: any;
  getStatusesTransactionById: any;
  getTransaction: () => Promise<void>;
  onClearTimer: (data?: boolean) => void;
  reset: UseFormReset<any>;
  handleClose: VoidFunction;
  onClear?: VoidFunction;
  chatTitle?: string;
  takeCount?: number;
  search: string;
  transactionListUrl: string;
  errors: FieldErrors;
  isDirty?: boolean;
  documents?: Document[];
  documentItem?: Document;
  setCollapseAllRows?: VoidFunction;
  updateIsError?: boolean;
  paymentType?: string;
  expandedGLTransaction?: GeneralLedgerTransaction;
  setUploadedFilesError?: (data: string[]) => void;
  exchangeChatTitle?: string;
  setRefundPopupVisible?: (data: boolean) => void;
  transactionUrl: string;
}) => {
  const {
    signatures: { mfaToken },
  } = useAppSelector((state) => state);

  const [imgLink, setImgLink] = useState('');
  const [deleteTransactionError, setDeleteTransactionError] = useState('');
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [preparedStatus, setPreparedStatus] = useState<
    BaseellaStatus | undefined
  >();
  const [statusChangeInProgress, setStatusChangeInProgress] = useState(false);
  const [statusPopupVisible, setStatusPopupVisible] = useState(false);
  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [needToCloseSidepanel, setNeedToCloseSidepanel] = useState(false);
  const [attachedDocumentsErrors, setAttachedDocumentsErrors] = useState<
    | Record<
        string,
        {
          name: string;
          error: string;
        }
      >
    | undefined
  >(undefined);
  const [uploadedDocumentsErrors, setUploadedDocumentsErrors] = useState<
    { name: string; error: string }[]
  >([]);

  const [
    deleteTransaction,
    {
      isLoading: deleteTransactionInProgress,
      isSuccess: deleteTransactionIsSuccess,
    },
  ] = useDeleteTransactionMutation();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteDocumentsMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeDocumentCategoryMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeDocumentStatusMutation();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetTransactionDocumentByIdQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetTransactionDocumentFileQuery();
  const [
    getTransactionDocuments,
    { isLoading: documentsIsLoading, isFetching: documentsIsFetching },
  ] = useLazyGetTransactionsDocumentsQuery();
  const [
    addDocumentsToTransaction,
    { isLoading: addDocumentInProgress, isSuccess: fileUploadIsSuccess },
  ] = useAddDocumentToTransactionMutation();
  const [
    getDebitAccountBalanceById,
    { isLoading: debitBalanceIsLoading, isFetching: debitBalanceIsFetching },
  ] = useLazyGetTransactionsAccountBalanceByIdQuery();
  const [
    getCreditAccountBalanceById,
    { isLoading: creditBalanceIsLoading, isFetching: creditBalanceIsFetching },
  ] = useLazyGetTransactionsAccountBalanceByIdQuery();
  const [getTransactionsCASP] = useLazyGetTransactionsCASPQuery();
  const [
    getGeneralLedger,
    { isLoading: glTransactionIsLoading, isFetching: glTransactionIsFetching },
  ] = useLazyGetGeneralLedgerExpandedTransactionQuery();
  const [getGeneralLedgerTransactionValues, { isLoading: glIsLoading }] =
    useLazyGetGeneralLedgerTransactionValuesQuery();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useTransactionsUploadMutation();
  const [getTransactionsChats, { isLoading: transactionChatsIsLoading }] =
    useLazyGetTransactionChatsQuery();
  const [getCompanyOfficials, { isLoading: cofIsLoading }] =
    useLazyGetClientsOfficiasQuery();
  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateTransactionChatMutation();
  const [getSignaturesTransactionById, { isLoading: isLoadingSignatures }] =
    useLazyGetTransactionSignatureQuery();
  const [
    createSignature,
    { isLoading: createSignatureInProgress, isSuccess: signatureIsSuccess },
  ] = useCreateTransactionSignatureMutation();
  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [
    getTransactionComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetTransactionCommentsQuery();
  const [
    addCommentToTransaction,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToTransactionMutation();
  const [
    editCommentInTransaction,
    {
      isLoading: editTransactionCommentInProgress,
      isSuccess: editTrancastionCommentIsSuccess,
    },
  ] = useEditCommentInTransactionMutation();

  const [getTransactionLogs, { isLoading: isLoadingLogs }] =
    useLazyGetTransactionLogsQuery();
  const [
    getTransactionChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionChangeLogsQuery();
  const [
    deleteCommentInTransaction,
    {
      isLoading: deleteCommentInTransactionInProgress,
      isSuccess: deleteCommentInTranscationIsSuccess,
    },
  ] = useDeleteCommentInTransactionMutation();

  const [
    changeStatus,
    {
      isSuccess: changeStatusIsSuccess,
      isLoading: changeStatusIsLoading,
      isError: changeStatusIsError,
    },
  ] = useChangeTransactionStatusMutation();

  const navigate = useNavigate();

  const onDeleteTransaction = async (id: string) => {
    const response = (await deleteTransaction(id)) as { error?: { data: any } };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError: setDeleteTransactionError,
      });
    }
  };

  const onAddCommentToTransaction = async (text: string) => {
    await addCommentToTransaction({
      id: transactionId as string,
      url: `transactions-${transactionUrl}`,
      body: {
        text,
      },
    });
  };

  const onEditCommentInTransaction = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInTransaction({
      id: transactionId as string,
      url: `transactions-${transactionUrl}`,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInTransaction = async (commentId: string) => {
    await deleteCommentInTransaction({
      id: transactionId as string,
      url: `transactions-${transactionUrl}`,
      commentId,
    });
  };

  const getTransactionCommentsAction = async (cursor?: string) => {
    await getTransactionComments({
      id: transactionId as string,
      url: `transactions-${transactionUrl}`,
      cursor: cursor || '',
    });
  };

  const getTransactionDocumentyIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onGlTransactionClick = useCallback((id: string) => {
    getGeneralLedger(id);
  }, []);

  const onUploadDocument = async (files: FileList, organizationId?: string) => {
    setAttachedDocumentsErrors(undefined);
    setUploadedDocumentsErrors([]);

    if (setUploadedFilesError) {
      setUploadedFilesError([]);
    }
    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const checkNameResponse = (await checkDocumentName({
          value: fileItem.name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = fileItem.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedDocumentsErrors(nextUploadedDocumentsErrors);

    if (setUploadedFilesError) {
      setUploadedFilesError(nextUploadedFilesErrors);
    }

    if (filesResponse && filesResponse.length) {
      const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

      const uploadedFiles: { name: string; uploadId: string }[] = [];

      filesResponse.forEach((item) => {
        if (item && item.name && item.id) {
          uploadedFiles.push({
            name: item?.name,
            uploadId: item?.id,
          });
        }
      });

      if (uploadedFiles.length) {
        await Promise.all(
          uploadedFiles.map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              organizationId?: string;
            } = {
              uploadId: item.uploadId,
              name: item.name,
            };

            if (organizationId) {
              body.organizationId = organizationId;
            }

            const response = (await addDocumentsToTransaction({
              id: transactionId as string,
              body,
            })) as {
              error?: {
                data: {
                  message?: {
                    constraints: Record<string, string>;
                  }[];
                };
              };
            };

            if (response.error && response?.error?.data?.message?.length) {
              const errorItem = response?.error?.data?.message[0].constraints;

              if (errorItem) {
                nextAttachedDocumentsErrors[item.uploadId] = {
                  name: item.name,
                  error: Object.values(errorItem)[0],
                };
              }
            }

            return response;
          }),
        );

        setAttachedDocumentsErrors(nextAttachedDocumentsErrors);
      }
    }
  };

  const onCreateTransactionChat = async (
    userIds?: string[],
    type?: 'recipient' | 'sender',
  ) => {
    setErrorMessage('');

    let title = `${chatTitle} ${formatBalance(
      transaction?.sum || 0,
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    if (exchangeChatTitle) {
      title = exchangeChatTitle;
    }

    const body: ChatBody = {
      title,
      employeeId: currentEmployee?.id as string,
    };

    if (type) {
      body.organizationId =
        type === 'recipient'
          ? transaction?.recipientAccount?.organizationId
          : transaction?.senderAccount?.organizationId;
    } else {
      body.organizationId = transaction?.recipientAccount?.organizationId;
    }

    if (!userIds?.length) {
      const response = (await createTransactionChat({
        id: transactionId as string,
        body,
      })) as { error: ResponseError };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      }
    } else {
      Promise.all(
        userIds.map((item) =>
          createTransactionChat({
            id: transactionId as string,
            body: {
              ...body,
              userId: item,
            },
          }),
        ),
      );
    }
  };

  const createChatWithMessage = async ({
    message,
    userId,
  }: {
    message: string;
    userId?: string;
  }) => {
    const title = `${chatTitle} ${formatBalance(
      transaction?.sum || 0,
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    const body: {
      title: string;
      employeeId: string;
      organizationId: string;
      userId?: string;
    } = {
      title,
      employeeId: currentEmployee?.id as string,
      organizationId: transaction?.recipientAccount?.organizationId,
    };

    if (userId) {
      body.userId = userId;
    }

    const response = (await createTransactionChat({
      id: transactionId as string,
      body,
    })) as { data: any };

    if (response && response.data && sendMessage) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: response?.data?.data?.id,
            content: message,
          },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
    }
  };

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        // eslint-disable-next-line react-hooks/purity
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          id: createdTransactionId || (transactionId as string),
          url: statusUrl,
          body: {
            type: SignatureType.TRANSACTION,
            mfaToken: response.data.data.token,
            signedAt: new Date().toISOString(),
          },
        })) as {
          error: { data: { message: string }; status: number };
        };
        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            responseErrorsResolver({
              errors: createResponse.error as ResponseError,
              setError,
              setGlobalError: setErrorMessageSignature,
            });
          }
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 403) {
          setErrorMessageSignature('Access denied');
        } else if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError: setErrorMessageSignature,
          });
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const getLogsTransactionByIdAction = useCallback(() => {
    getTransactionLogs({ id: transactionId as string, url: transactionUrl });
  }, [transactionId, transaction]);

  const getTransactionChangeLogsAction = useCallback(
    (cursor?: string) => {
      getTransactionChangeLogs(
        `?take=20&entityType=TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const onClearRequestTimer = async () => {
    await clearTimeout(timer);
  };

  const onClearRequestTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onChangeStatusPopup = async () => {
    setStatusChangeError('');

    if (transactionId && preparedStatus) {
      const saveResponse = await onSubmit();

      clearTimeout(timeout);
      onClearTimer();

      if (saveResponse && saveResponse.data) {
        if (
          preparedStatus === BaseellaStatus.REJECTED ||
          preparedStatus === BaseellaStatus.AML_REJ
        ) {
          setStatusPopupVisible(false);
          setRejectPopupVisible(true);
        } else if (
          preparedStatus === BaseellaStatus.REFUNDED &&
          setRefundPopupVisible
        ) {
          setRefundPopupVisible(true);
        } else {
          setStatusChangeInProgress(true);

          const response = (await changeStatus({
            url: statusUrl,
            id: transactionId,
            body: {
              baseellaStatusFrom: transaction?.latestQueuedStatuses
                ?.baseellaStatus as BaseellaStatus,
              baseellaStatusTo: preparedStatus,
            },
          })) as { error?: { data: { message: string } } };

          if (response) {
            if (!response.error) {
              if (preparedStatus !== BaseellaStatus.ARCH) {
                const statusesResponse = await getStatusesTransactionById({
                  id: transactionId,
                  url: statusUrl,
                  baseellaStatusFrom: transaction?.latestQueuedStatuses
                    ?.baseellaStatus as string,
                });

                if (statusesResponse) {
                  setStatusChangeInProgress(false);

                  setPreparedStatus(undefined);
                  setStatusPopupVisible(false);

                  timeout = setTimeout(() => {
                    getTransaction();
                  }, 100);
                }
              } else {
                if (onClear) {
                  onClear();
                }

                onClearTimer(true);
                clearTimeout(timeout);
                reset({});

                handleClose();
                clearTimeout(timer);

                if (transactionId) {
                  getTransactionsList(
                    `?take=${takeCount?.toString() || '25'}${search.replace(
                      '?',
                      '&',
                    )}`,
                  );
                }
              }
            } else {
              setStatusChangeInProgress(false);
              setStatusPopupVisible(false);

              if (response.error) {
                responseErrorsResolver({
                  errors: response.error as ResponseError,
                  setError,
                  setGlobalError: setStatusChangeError,
                });
              }
            }
          }
        }
      }
    }
  };

  const onSignatureCancelClick = () => {
    clearTimeout(timer);
    setSignaturePopupVisible(false);

    if (!transactionId) {
      navigate(
        `${generatePath(transactionListUrl, {
          transactionId: createdTransactionId,
        })}${search || ''}`,
      );
    }
  };

  const onChangeStatus = async (status: string) => {
    setPreparedStatus(status as BaseellaStatus);
    setStatusChangeError('');

    if (transactionId && !Object.keys(errors)?.length) {
      if (isDirty) {
        setStatusPopupVisible(true);
      } else {
        clearTimeout(timeout);
        onClearTimer();

        if (
          status === BaseellaStatus.REJECTED ||
          status === BaseellaStatus.AML_REJ
        ) {
          setRejectPopupVisible(true);
        } else if (
          status === BaseellaStatus.REFUNDED &&
          setRefundPopupVisible
        ) {
          setRefundPopupVisible(true);
        } else {
          setStatusChangeInProgress(true);

          const response = (await changeStatus({
            url: statusUrl,
            id: transactionId,
            body: {
              baseellaStatusFrom: transaction?.latestQueuedStatuses
                ?.baseellaStatus as BaseellaStatus,
              baseellaStatusTo: status as BaseellaStatus,
            },
          })) as { error?: { data: { message: string } } };

          if (response) {
            if (!response.error) {
              if (
                status !== BaseellaStatus.ARCH &&
                status !== BaseellaStatus.AML_REJ &&
                status !== BaseellaStatus.REJECTED
              ) {
                const statusesResponse = await getStatusesTransactionById({
                  id: transactionId,
                  url: statusUrl,
                  baseellaStatusFrom: transaction?.latestQueuedStatuses
                    ?.baseellaStatus as string,
                });

                if (statusesResponse) {
                  setStatusChangeInProgress(false);

                  timeout = setTimeout(() => {
                    getTransaction();
                  }, 100);
                }
              } else {
                if (onClear) {
                  onClear();
                }

                onClearTimer(true);
                clearTimeout(timeout);
                reset({});

                handleClose();

                if (transactionId) {
                  getTransactionsList(
                    `?take=${takeCount?.toString() || '25'}${search.replace(
                      '?',
                      '&',
                    )}`,
                  );
                }
              }
            } else {
              setStatusChangeInProgress(false);

              if (response.error) {
                responseErrorsResolver({
                  errors: response.error as ResponseError,
                  setError,
                  setGlobalError: setStatusChangeError,
                });
              }
            }
          }
        }
      }
    }
  };

  const getTransationDocumentsAction = (cursor?: string) => {
    const body: {
      id: string;
      cursor?: string;
    } = {
      id: transactionId as string,
    };

    if (cursor) {
      body.cursor = cursor;
    }

    getTransactionDocuments(body);
  };

  const getGeneralLedgerTransactionValuesAction = () => {
    getGeneralLedgerTransactionValues(transactionId as string);
  };

  const getTransactionsChatsAction = () => {
    getTransactionsChats(transactionId as string);
  };

  const getCompanyOfficialsAction = (id: string) => {
    getCompanyOfficials(id);
  };

  const getSignaturesTransactionByIdAction = () => {
    getSignaturesTransactionById({
      id: transactionId as string,
      url: statusUrl,
    });
  };

  const onRefund = async () => {
    setStatusChangeError('');

    if (transactionId) {
      clearTimeout(timeout);

      onClearTimer();
      setStatusChangeInProgress(true);

      const response = (await changeStatus({
        url: statusUrl,
        id: transactionId,
        body: {
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as BaseellaStatus,
          baseellaStatusTo: preparedStatus || BaseellaStatus.REFUNDED,
        },
      })) as { data: any; error?: { data: { message: string } } };

      if (response) {
        if (!response.error) {
          if (response.data) {
            getTransactionsList(
              `?take=${takeCount?.toString() || '25'}${search.replace(
                '?',
                '&',
              )}`,
            );
            setRejectPopupVisible(false);
            setPreparedStatus(undefined);

            const statusesResponse = await getStatusesTransactionById({
              id: transactionId,
              url: statusUrl,
              baseellaStatusFrom: transaction?.latestQueuedStatuses
                ?.baseellaStatus as string,
            });

            if (statusesResponse) {
              setStatusChangeInProgress(false);

              timeout = setTimeout(() => {
                getTransaction();
              }, 100);
            }
          }

          setNeedToCloseSidepanel(true);
        } else {
          setStatusChangeInProgress(false);
          setRejectPopupVisible(false);

          if (response.error) {
            responseErrorsResolver({
              errors: response.error as ResponseError,
              setError,
              setGlobalError: setStatusChangeError,
            });
          }
        }
      }
    }
  };

  const onReject = async () => {
    setStatusChangeError('');

    if (transactionId) {
      clearTimeout(timeout);

      onClearTimer();
      setStatusChangeInProgress(true);

      const response = (await changeStatus({
        url: statusUrl,
        id: transactionId,
        body: {
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as BaseellaStatus,
          baseellaStatusTo: preparedStatus || BaseellaStatus.REJECTED,
        },
      })) as { data: any; error?: { data: { message: string } } };

      if (response) {
        if (!response.error) {
          if (response.data) {
            getTransactionsList(
              `?take=${takeCount?.toString() || '25'}${search.replace(
                '?',
                '&',
              )}`,
            );
            setRejectPopupVisible(false);
            setPreparedStatus(undefined);

            const statusesResponse = await getStatusesTransactionById({
              id: transactionId,
              url: statusUrl,
              baseellaStatusFrom: transaction?.latestQueuedStatuses
                ?.baseellaStatus as string,
            });

            if (statusesResponse) {
              setStatusChangeInProgress(false);

              timeout = setTimeout(() => {
                getTransaction();
              }, 100);
            }
          }
        } else {
          setStatusChangeInProgress(false);
          setRejectPopupVisible(false);

          if (response.error) {
            responseErrorsResolver({
              errors: response.error as ResponseError,
              setError,
              setGlobalError: setStatusChangeError,
            });
          }
        }
      }
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };

  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const sendCodeRetry = (expireTime: number) => {
    timer = setTimeout(() => {
      onSendCode();
    }, expireTime);
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: documentItem?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  useEffect(() => {
    if (deleteTransactionIsSuccess) {
      handleClose();

      getTransactionsList(
        `?take=${takeCount?.toString() || '25'}${search.replace('?', '&')}`,
      );

      if (setCollapseAllRows) {
        setCollapseAllRows();
      }
    }
  }, [deleteTransactionIsSuccess, takeCount, search]);

  useEffect(() => {
    if (changeStatusIsError || updateIsError) {
      setStatusPopupVisible(false);
    }
  }, [changeStatusIsError, updateIsError]);

  useEffect(() => {
    if (paymentType && paymentType === 'CRYPTO_ASSET_PAYMENT') {
      getTransactionsCASP();
    }
  }, [paymentType]);

  useEffect(() => {
    if (expandedGLTransaction) {
      getDebitAccountBalanceById({
        id: expandedGLTransaction?.debitAccountId as string,
        query: `&currencyId=${expandedGLTransaction?.currencyId}`,
        typeAcc: AccountType.DEBIT,
      });
      getCreditAccountBalanceById({
        id: expandedGLTransaction?.creditAccountId as string,
        query: `&currencyId=${expandedGLTransaction?.currencyId}`,
        typeAcc: AccountType.CREDIT,
      });
    }
  }, [expandedGLTransaction]);

  return {
    imgLink,
    setImgLink,
    deleteTransactionError,
    setDeleteTransactionError,
    setUploadedFilesError,
    errorMessageSignature,
    setErrorMessageSignature,
    mfaToken,
    signaturePopupVisible,
    setSignaturePopupVisible,
    statusChangeInProgress,
    setStatusChangeInProgress,
    statusPopupVisible,
    setStatusPopupVisible,
    rejectPopupVisible,
    setRejectPopupVisible,
    deleteTransactionInProgress,
    deleteTransactionIsSuccess,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    commentInProgress,
    documentCategoryChangeInProgress,
    documentStatusChangeInProgress,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    documentsIsLoading,
    addDocumentInProgress,
    fileUploadIsSuccess,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    glTransactionIsLoading,
    glTransactionIsFetching,
    glIsLoading,
    fileUploadInProgress: checkDocumentNameInProgress || fileUploadInProgress,
    transactionChatsIsLoading,
    cofIsLoading,
    chatCreateInProgress,
    isLoadingSignatures,
    createSignatureInProgress,
    signatureIsSuccess,
    sendCodeInProgress,
    validateCodeInProgress,
    changeStatusIsSuccess,
    changeStatusIsLoading,
    changeStatusIsError,
    isLoadingLogs,
    onDeleteTransaction,
    getTransactionDocumentyIdAction,
    onGlTransactionClick,
    onUploadDocument,
    onCreateTransactionChat,
    createChatWithMessage,
    onSendCode,
    validateCodeAction,
    getLogsTransactionByIdAction,
    onClearRequestTimer,
    onChangeStatusPopup,
    onSignatureCancelClick,
    onChangeStatus,
    getTransationDocumentsAction,
    getGeneralLedgerTransactionValuesAction,
    getTransactionsChatsAction,
    getCompanyOfficialsAction,
    getSignaturesTransactionByIdAction,
    onReject,
    onDeleteDocument,
    onMoveDocument,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onChangeCustomerDocumentsStatus,
    sendCodeRetry,
    sendCode,
    onClearRequestTimeout,
    getTransactionChangeLogsAction,
    changeLogsIsLoading,
    changeLogsIsFetching,
    onRefund,
    needToCloseSidepanel,
    documentsIsFetching,
    attachedDocumentsErrors,
    setAttachedDocumentsErrors,
    uploadedDocumentsErrors,
    setUploadedDocumentsErrors,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
  };
};

export default useBaseRequests;
